import React, { useCallback, useState } from 'react';
import { useSupabaseData } from '../../hooks/useSupabaseData';
import { useEvents } from '../../hooks/useEvents';
import { useI18n } from '../../context/I18nContext';
import { useToast } from '../../context/ToastContext';
import { calendarService, leadsService, projectsService } from '../../services/supabase';
import { exportToCSV, formatDateTimeForExport } from '../../utils/exportUtils';
import PageHeader from '../../components/ui/PageHeader';
import { PAGE_GAP } from '../../styles/pageLayout';
import Spinner from '../../components/ui/Spinner';
import Calendar from '../../components/calendar/Calendar';
import Card from '../../components/ui/Card';
import { Project } from '../../types';
import { useCreateLeadFromSearch } from '../../hooks/useCreateLeadFromSearch';

const CalendarPage: React.FC = () => {
  const { t } = useI18n();
  const { showToast, showToastWithUndo } = useToast();
  const { requestCreateLead, leadCreateModal } = useCreateLeadFromSearch();
  const { events, loadEventsInRange, addEvent, updateEvent, deleteEvent } = useEvents({
    onUpdateFailed: () => showToast(t('common.saveFailedRollback'), 'error'),
  });
  const { leads, updateLeadNextActionAt, loadLeadFollowupsInRange, loading, error } = useSupabaseData({
    // Follow-ups load via loadLeadFollowupsInRange on datesSet — avoid full-table getAll
    include: { leads: false, projects: false, categories: false },
  });
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [rightsProjects, setRightsProjects] = useState<Project[]>([]);

  const handleAddEvent = (event: any) => {
    // Optimistic UI - instant feedback
    addEvent(event);
  };

  const handleUpdateEvent = async (event: any, options?: { previousItem?: any }) => {
    // If updating a lead-related event, sync the lead's nextActionAt directly via service
    if (event.type === 'lead_update' && event.leadId) {
      const res = await updateLeadNextActionAt(event.leadId, event.nextActionAt);
      if (!res.ok) throw new Error('Failed to update lead follow-up date');
      return;
    }
    // Optimistic UI - instant feedback (with optional previousItem for local rollback)
    updateEvent(event, options);
  };

  const handleDeleteEvent = (id: string) => {
    const eventToDelete = events.find(e => e.id === id);
    if (!eventToDelete) return;

    // Store the event data for undo
    const eventData = { ...eventToDelete };
    
    // Delete with optimistic UI (instant feedback)
    deleteEvent(id);
    
    // Show toast with undo immediately
    showToastWithUndo(
      t('calendar.eventDeleted', { title: eventToDelete.title }) || 
      `Événement "${eventToDelete.title}" supprimé`,
      async () => {
        // Undo: recreate the event (optimistic, no global refresh)
        addEvent({
          title: eventData.title,
          startDate: eventData.startDate,
          endDate: eventData.endDate,
          type: eventData.type,
          leadId: eventData.leadId,
          description: eventData.description,
          color: eventData.color,
          allDay: eventData.allDay,
        });
      },
      'error'
    );
  };

  const handleExport = async () => {
    try {
      // Fetch ALL events directly from service to ensure we get everything
      const allEvents = await calendarService.getAll();

      if (allEvents.length === 0) {
        showToast(t('calendar.noEventsToExport') || 'Aucun événement à exporter', 'warning');
        return;
      }

      const exportHeaders = [
        t('calendar.eventTitle'),
        t('calendar.eventType'),
        t('calendar.associatedLead'),
        t('calendar.startDateLabel'),
        t('calendar.endDateLabel'),
        t('calendar.eventDescription'),
        t('calendar.eventColor')
      ];

      const exportData = allEvents.map(event => {
        const lead = leads.find(l => l.id === event.leadId);
        return {
          [t('calendar.eventTitle')]: event.title,
          [t('calendar.eventType')]: event.type,
          [t('calendar.associatedLead')]: lead?.name || '',
          [t('calendar.startDateLabel')]: formatDateTimeForExport(event.startDate),
          [t('calendar.endDateLabel')]: event.endDate != null ? formatDateTimeForExport(event.endDate) : '',
          [t('calendar.eventDescription')]: event.description || '',
          [t('calendar.eventColor')]: event.color || ''
        };
      });

      exportToCSV(exportData, `calendar-${new Date().toISOString().split('T')[0]}`, exportHeaders);
      showToast(t('common.exportSuccess', { count: exportData.length }) || `${exportData.length} événements exportés`, 'success');
    } catch (error) {
      console.error('Error exporting calendar:', error);
      showToast(t('calendar.exportError') || 'Erreur lors de l\'export du calendrier', 'error');
    }
  };

  const handleViewDatesChange = useCallback((start: Date, end: Date) => {
    loadLeadFollowupsInRange(start, end);
    loadEventsInRange(start, end);
    projectsService.getWithRightsEndInRange(start, end)
      .then(setRightsProjects)
      .catch((err) => {
        console.error('Error loading rights end dates:', err);
        setRightsProjects([]);
      });
  }, [loadLeadFollowupsInRange, loadEventsInRange]);

  const handleSearchLeads = useCallback((term: string) => {
    return leadsService.search(term, 10);
  }, []);

  const handleOpenNewEventModal = () => {
    setIsCalendarModalOpen(true);
  };

  // Progressive loading: keep shell visible; loaders are local to the calendar section.

  return (
    <div className={`w-full flex flex-col flex-1 min-h-0 ${PAGE_GAP} overflow-hidden`}>
      <PageHeader
        title={t('calendar.title')}
        description={t('calendar.manageCalendar')}
        actionsConfig={{
          newButtonKey: 'calendar.newEvent',
          onCreateNew: handleOpenNewEventModal,
          onExport: handleExport,
          newButtonFullWidthOnMobile: true,
        }}
      />

      {error ? (
        <Card className="flex-1 min-h-0 overflow-hidden">
          <div className="flex-1 flex items-center justify-center text-center px-4">
            <div>
              <p className="text-red-600 font-medium">{t('common.error')}</p>
              <p className="text-gray-500 mt-1">{error}</p>
            </div>
          </div>
        </Card>
      ) : loading ? (
        <Card className="flex-1 min-h-0 overflow-hidden">
          <div className="flex-1 flex items-center justify-center">
            <Spinner size="md" label={t('common.loading')} />
          </div>
        </Card>
      ) : (
        <Calendar
          events={events}
          leads={leads}
          rightsProjects={rightsProjects}
          onAddEvent={handleAddEvent}
          onUpdateEvent={handleUpdateEvent}
          onDeleteEvent={handleDeleteEvent}
          isModalOpen={isCalendarModalOpen}
          onModalClose={() => setIsCalendarModalOpen(false)}
          onViewDatesChange={handleViewDatesChange}
          onSearchLeads={handleSearchLeads}
          onCreateLead={requestCreateLead}
        />
      )}
      {leadCreateModal}
    </div>
  );
};

export default CalendarPage;
